"use client"

import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useAvatarRive } from "./hooks/useAvatarRive"
import { useSegmentedNarration } from "./hooks/useSegmentedNarration"
import { parseSRT } from "./SRTParser"
import EnhancedAudioControls from "./EnhancedAudioControls"
import Confetti from "./components/Confetti"
import SuperSimpleSynchronizer from "./SuperSimpleSynchronizer"
import { getMediaPlayerMode } from "./MediaPlayerModes"
import "./css/TeachingAvatar.css"
import { PasswordPrompt } from "./components/PasswordPrompt"
import { usePasswordCheck } from "./hooks/usePasswordCheck"

const MAX_STATES = {
  idle: { input: "idle", type: "trigger" },
  wave: { input: "wave", type: "trigger" },
  jump: { input: "Jump", type: "trigger" },
  really: { input: "Really", type: "trigger" },
  bow: { input: "Bow", type: "trigger" },
  defeated: { input: "defeated", type: "trigger" },
  happy: { input: "happy", type: "trigger" },
  sad: { input: "sad", type: "trigger" },
  follow: { input: "idle", type: "trigger" },
  smile: { input: "smile", type: "trigger" },
  talk: { input: "talk", type: "trigger" },
  hover: { input: "hover", type: "trigger" },
  thinking: { input: "thinking", type: "trigger" },
}

const TeachingAvatar = ({
  mainContent,
  visualContent,
  spokenContent,
  quizOptions,
  onSelectAnswer,
  selectedAnswer,
  isCorrect,
  isChecking,
  isCelebration,
  isSuccess,
  audioSrc,
  srtSrc,
  initialState = "idle",
  state,
  audioControlsRef: externalAudioControlsRef,
  initialControlsVisible = true,
  lessonId,
  stepLocked,
  hideAudioControls = false,
  onUnlock,
  avatar = "bella", // Default narrator
  avatarAlignment = "right", // Optional: "left", "centered", "right"
  avatarPosition, // Optional: { bottom, right, left, top } in rem or px
  avatarScale = 1.0, // Optional: scale multiplier (e.g., 1.2 for 20% larger)
  mobilePosition, // Optional: { right, bottom } for mobile-specific positioning
  mobileScale, // Optional: scale multiplier for mobile (e.g., 1.5 for 50% larger on mobile)
  isVisualTheme, // Declare the variable here
}) => {
  const [showSubtitles, setShowSubtitles] = useState(true)
  const [currentCaption, setCurrentCaption] = useState(spokenContent || "")
  const [subtitles, setSubtitles] = useState([])
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [currentAudioTime, setCurrentAudioTime] = useState(0)
  const [isAudioPlaying, setIsAudioPlaying] = useState(false)
  const [currentLine, setCurrentLine] = useState(0)
  const [totalLines, setTotalLines] = useState(1)
  const [showControls, setShowControls] = useState(true)
  const [isSrtOnlyMode, setIsSrtOnlyMode] = useState(false)
  const [playerMode, setPlayerMode] = useState("text-only")
  const [isMobile, setIsMobile] = useState(false)
  const [showFeedback, setShowFeedback] = useState(false)
  const [isStepUnlocked, setIsStepUnlocked] = useState(false)
  const [showPlayButton, setShowPlayButton] = useState(false)
  const [isPlayButtonFading, setIsPlayButtonFading] = useState(false)

  const { checkPassword, isLessonUnlocked } = usePasswordCheck()

  const videoContainerRef = useRef(null)
  const internalAudioControlsRef = useRef(null)
  const bubbleRef = useRef(null)
  const contentContainerRef = useRef(null)
  const lastContentRef = useRef({
    mainContent,
    audioSrc,
    srtSrc,
    isCelebration: false,
    isSuccess: false,
  })
  const srtTimerRef = useRef(null)
  const srtStartTimeRef = useRef(0)
  const lessonIdRef = useRef(lessonId)

  const audioControlsRef = externalAudioControlsRef || internalAudioControlsRef

const { rive, RiveComponent } = useAvatarRive(avatar, "State Machine 1")


  const isStepLocked = useCallback(() => {
    if (isStepUnlocked) return false

    return (
      stepLocked === true ||
      mainContent?.locked === true ||
      visualContent?.locked === true ||
      spokenContent?.locked === true
    )
  }, [isStepUnlocked, stepLocked, mainContent?.locked, visualContent?.locked, spokenContent?.locked])

  const handleStepPasswordSubmit = (password) => {
    const unlocked = checkPassword(password, lessonIdRef.current)
    setIsStepUnlocked(unlocked)
    if (unlocked && onUnlock) {
      onUnlock()
    }
  }

  const currentStepData = visualContent?.steps?.[0] || visualContent
  const videoSrc =
    currentStepData?.videoUrl || currentStepData?.videoSrc || visualContent?.videoUrl || visualContent?.videoSrc
  const videoType = currentStepData?.videoType || visualContent?.videoType || (videoSrc ? "mp4" : null)

  // ── Narration ─────────────────────────────────────────────────────────
  // The caption advances line-by-line (one SRT cue). The VOICE is one TTS
  // clip per *sentence* — consecutive cues merged up to a . ! ? — so it
  // reads naturally instead of clipping every fragment. Within a sentence
  // the caption is paced by each cue's share of the text.
  const segments =
    subtitles && subtitles.length
      ? subtitles.map((s) => s.text)
      : (spokenContent || "")
          .trim()
          .match(/[^.!?]+[.!?]*/g)
          ?.map((s) => s.trim())
          .filter(Boolean) || (spokenContent ? [String(spokenContent).trim()] : [])

  const voiceGroups = useMemo(() => {
    const groups = []
    let cur = null
    const seal = () => {
      cur.text = cur.parts.join(" ").replace(/\s+/g, " ").trim()
      cur.lens = cur.parts.map((p) => p.trim().length || 1)
      cur.total = cur.lens.reduce((a, b) => a + b, 0)
      groups.push(cur)
      cur = null
    }
    segments.forEach((text, i) => {
      if (!cur) cur = { firstCue: i, lastCue: i, parts: [text] }
      else {
        cur.lastCue = i
        cur.parts.push(text)
      }
      const joined = cur.parts.join(" ")
      if (/[.!?]["'”’)\]]?\s*$/.test(text.trim()) || joined.length >= 220) seal()
    })
    if (cur) seal()
    return groups
  }, [segments.join("")])

  const narrationEnabled =
    !hideAudioControls &&
    !videoSrc &&
    !isCelebration &&
    !isSuccess &&
    segments.length > 0 &&
    // when the step has an SRT, wait for it so segments are the real cues
    (subtitles.length > 0 || !srtSrc)

  const currentGroupIndex = Math.max(
    0,
    voiceGroups.findIndex((g) => currentLine >= g.firstCue && currentLine <= g.lastCue),
  )
  const groupAudioSrc = useSegmentedNarration(
    voiceGroups.map((g) => g.text),
    currentGroupIndex,
    narrationEnabled,
  )
  // Feeds the <audio>. `audioSrc` prop still identifies the step (change
  // detection); this is what plays right now.
  const playbackSrc = narrationEnabled ? groupAudioSrc : audioSrc
  const effectiveTotalLines = narrationEnabled ? segments.length : totalLines
  // The audio player advances (and auto-advances on "ended") by sentence in
  // narration mode — one clip per voice group.
  const hasNextClip = narrationEnabled
    ? currentGroupIndex < voiceGroups.length - 1
    : currentLine < totalLines - 1
  const hasPrevClip = narrationEnabled ? currentGroupIndex > 0 : currentLine > 0

  // The current voice clip's real duration, for pacing the caption inside a
  // multi-cue sentence. Reset whenever the clip (group) changes.
  const [groupDuration, setGroupDuration] = useState(0)
  useEffect(() => {
    setGroupDuration(0)
  }, [currentGroupIndex])

  useEffect(() => {
    const mode = narrationEnabled
      ? srtSrc
        ? "audio+srt"
        : "audio-only"
      : getMediaPlayerMode(audioSrc || videoSrc, srtSrc, videoType)
    setPlayerMode(mode)
    setIsSrtOnlyMode(mode === "srt-only")
  }, [narrationEnabled, audioSrc, videoSrc, srtSrc, videoType])

  // Caption follows the cue index, not SRT playback time.
  useEffect(() => {
    if (!narrationEnabled) return
    setCurrentCaption(segments[currentLine] || "")
  }, [narrationEnabled, currentLine, segments])

  // Inside a multi-cue sentence, step the caption forward by each cue's
  // share of the sentence text, scaled to the clip's real duration.
  useEffect(() => {
    if (!narrationEnabled || !isAudioPlaying || !groupDuration) return
    const g = voiceGroups[currentGroupIndex]
    if (!g || g.firstCue === g.lastCue) return
    const frac = Math.min(1, currentAudioTime / groupDuration)
    let acc = 0
    let target = g.firstCue
    for (let k = 0; k < g.lens.length; k++) {
      acc += g.lens[k] / g.total
      target = g.firstCue + k
      if (frac < acc) break
    }
    if (target > currentLine && target <= g.lastCue) setCurrentLine(target)
  }, [narrationEnabled, isAudioPlaying, currentAudioTime, groupDuration, currentGroupIndex, currentLine, voiceGroups])

  useEffect(() => {
    if (lessonIdRef.current !== lessonId) {
      setShowSubtitles(true)
      setShowControls(true)

      lessonIdRef.current = lessonId
    }
  }, [lessonId, initialControlsVisible])

  useEffect(() => {
    const checkIsMobile = () => {
      const mobile = window.innerWidth < 768
      setIsMobile(mobile)
    }

    checkIsMobile()

    window.addEventListener("resize", checkIsMobile)

    return () => {
      window.removeEventListener("resize", checkIsMobile)
    }
  }, [isVisualTheme])

  useEffect(() => {
    const contentChanged =
      lastContentRef.current.mainContent !== mainContent ||
      lastContentRef.current.audioSrc !== audioSrc ||
      lastContentRef.current.srtSrc !== srtSrc ||
      lastContentRef.current.isCelebration !== isCelebration ||
      lastContentRef.current.isSuccess !== isSuccess

    if (contentChanged) {
      console.log("[v0] Content changed, resetting audio state")
      setIsPlayButtonFading(false) // Reset play button state on content change (navigation)
      setSubtitles([])
      setCurrentCaption(spokenContent || "")
      setCurrentLine(0)
      setTotalLines(1)
      setCurrentAudioTime(0)
      setIsAudioPlaying(false)
      setShowFeedback(false)

      setShowSubtitles(true)

      lastContentRef.current = {
        mainContent,
        audioSrc,
        srtSrc,
        isCelebration,
        isSuccess,
      }

      if (srtTimerRef.current) {
        clearInterval(srtTimerRef.current)
        srtTimerRef.current = null
      }
    }
  }, [mainContent, audioSrc, srtSrc, spokenContent, isCelebration, isSuccess])

  useEffect(() => {
    if (isChecking && selectedAnswer !== null && quizOptions) {
      setShowFeedback(true)
    } else if (!isChecking) {
      setShowFeedback(false)
    }
  }, [isChecking, selectedAnswer, quizOptions])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (isStepLocked()) {
        if (["ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Enter", " ", "Tab"].includes(event.key)) {
          event.preventDefault()
          event.stopPropagation()
        }
      }
    }

    document.addEventListener("keydown", handleKeyDown, true)
    return () => {
      document.removeEventListener("keydown", handleKeyDown, true)
    }
  }, [isStepLocked])

  const updateAvatarState = (isTalking) => {
    if (!rive) return

    const stateMachineInputs = rive.stateMachineInputs("State Machine 1")
    if (!stateMachineInputs) return

    if (isTalking) {
      const input = stateMachineInputs.find((input) => input.name === "talk")
      if (input) {
        input.fire()
      }
    } else if (state) {
      const stateConfig = MAX_STATES[state]

      if (stateConfig) {
        const stateMachineInputs = rive.stateMachineInputs("State Machine 1")
        if (!stateMachineInputs) {
          return
        }

        const input = stateMachineInputs.find((input) => input.name === stateConfig.input)

        if (input) {
          input.fire()
        } else {
          console.log("[v0] ERROR: Rive input not found:", stateConfig.input)
          console.log(
            "[v0] Available Rive inputs:",
            stateMachineInputs.map((i) => i.name),
          )
        }
      } else {
        console.log("[v0] ERROR: No state config found for state:", state)
      }
    } else if (rive && isAudioPlaying) {
      updateAvatarState(true)
    }
  }

  useEffect(() => {
    if (rive && state) {
      const stateConfig = MAX_STATES[state]

      if (stateConfig) {
        const stateMachineInputs = rive.stateMachineInputs("State Machine 1")
        if (!stateMachineInputs) {
          return
        }

        const input = stateMachineInputs.find((input) => input.name === stateConfig.input)

        if (input) {
          input.fire()
        } else {
          console.log("[v0] ERROR: Rive input not found:", stateConfig.input)
          console.log(
            "[v0] Available Rive inputs:",
            stateMachineInputs.map((i) => i.name),
          )
        }
      } else if (rive && isAudioPlaying) {
        updateAvatarState(true)
      }
    }
  }, [rive, state, isAudioPlaying])

  useEffect(() => {
    const loadSubtitles = async () => {
      if (!srtSrc) return

      try {
        const response = await fetch(srtSrc)
        const srtContent = await response.text()
        const parsedSubtitles = parseSRT(srtContent)

        if (lastContentRef.current.srtSrc === srtSrc) {
          setSubtitles(parsedSubtitles)
          if (parsedSubtitles.length > 0) {
            setCurrentCaption(parsedSubtitles[0].text)
            setTotalLines(parsedSubtitles.length)
          }
        }
      } catch (error) {
        console.error("Error loading subtitles:", error)
      }
    }

    loadSubtitles()
  }, [srtSrc])

  useEffect(() => {
    if (playerMode !== "srt-only" || !isAudioPlaying) {
      if (srtTimerRef.current) {
        clearInterval(srtTimerRef.current)
        srtTimerRef.current = null
      }
      return
    }

    srtStartTimeRef.current = Date.now() - currentAudioTime * 1000

    srtTimerRef.current = setInterval(() => {
      const newTime = (Date.now() - srtStartTimeRef.current) / 1000
      setCurrentAudioTime(newTime)

      if (subtitles.length > 0) {
        const lastSub = subtitles[subtitles.length - 1]
        if (lastSub && newTime > lastSub.end + 1) {
          setIsAudioPlaying(false)
          clearInterval(srtTimerRef.current)
          srtTimerRef.current = null
          updateAvatarState(false)
        }
      }
    }, 100)

    return () => {
      if (srtTimerRef.current) {
        clearInterval(srtTimerRef.current)
        srtTimerRef.current = null
      }
    }
  }, [playerMode, isAudioPlaying, subtitles, currentAudioTime])

  useEffect(() => {
    if (narrationEnabled) return // segment index drives the caption instead
    if (!subtitles || subtitles.length === 0) return

    const currentSub = subtitles.find((sub) => currentAudioTime >= sub.start && currentAudioTime <= sub.end)

    if (currentSub) {
      setCurrentCaption(currentSub.text)
      const lineIndex = subtitles.findIndex((sub) => sub.text === currentSub.text)
      if (lineIndex !== -1 && lineIndex !== currentLine) {
        setCurrentLine(lineIndex)
      }
    } else if (!spokenContent) {
      if (currentAudioTime < subtitles[0].start) {
        setCurrentCaption(subtitles[0].text)
        setCurrentLine(0)
      } else if (currentAudioTime > subtitles[subtitles.length - 1].end) {
        setCurrentCaption(subtitles[subtitles.length - 1].text)
        setCurrentLine(subtitles.length - 1)
      }
    }
  }, [narrationEnabled, currentAudioTime, subtitles, currentLine, spokenContent])

  useEffect(() => {
    if (bubbleRef.current && isAudioPlaying) {
      const pulse = setInterval(() => {
        if (bubbleRef.current) {
          bubbleRef.current.classList.add("transition-transform")
          bubbleRef.current.classList.add("scale-105")

          setTimeout(() => {
            if (bubbleRef.current) {
              bubbleRef.current.classList.remove("scale-105")
            }
          }, 500)
        }
      }, 5000)

      return () => clearInterval(pulse)
    }
  }, [isAudioPlaying])

  useEffect(() => {
    if (externalAudioControlsRef && externalAudioControlsRef.current) {
      externalAudioControlsRef.current = {
        pause: () => {
          setIsAudioPlaying(false)

          if (
            audioControlsRef.current &&
            audioControlsRef.current.pause &&
            audioControlsRef.current !== externalAudioControlsRef.current
          ) {
            audioControlsRef.current.pause()
          }

          if (srtTimerRef.current) {
            clearInterval(srtTimerRef.current)
            srtTimerRef.current = null
          }

          updateAvatarState(false)
        },
        play: async () => {
          setIsAudioPlaying(true)

          setShowSubtitles(true)

          if (
            audioControlsRef.current &&
            audioControlsRef.current.play &&
            audioControlsRef.current !== externalAudioControlsRef.current
          ) {
            await audioControlsRef.current.play()
          } else if (playerMode === "srt-only" && !srtTimerRef.current) {
            srtStartTimeRef.current = Date.now() - currentAudioTime * 1000

            updateAvatarState(true)

            srtTimerRef.current = setInterval(() => {
              const newTime = (Date.now() - srtStartTimeRef.current) / 1000
              setCurrentAudioTime(newTime)

              if (subtitles.length > 0) {
                const lastSub = subtitles[subtitles.length - 1]
                if (lastSub && newTime > lastSub.end + 1) {
                  setIsAudioPlaying(false)
                  clearInterval(srtTimerRef.current)
                  srtTimerRef.current = null
                  updateAvatarState(false)
                }
              }
            }, 100)
          }
        },
        nextLine: () => {
          handleNavigate("next")
        },
        prevLine: () => {
          handleNavigate("prev")
        },
        forceShowSubtitles: () => {
          setShowSubtitles(true)
        },
      }
    }
  }, [externalAudioControlsRef, showControls, audioSrc, playerMode, currentAudioTime, subtitles])

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      videoContainerRef.current?.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement)
    }

    document.addEventListener("fullscreenchange", handleFullscreenChange)
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange)
    }
  }, [])

  const handleTimeUpdate = (currentTime, duration) => {
    setCurrentAudioTime(currentTime)
    if (duration && isFinite(duration) && duration > 0) setGroupDuration(duration)
  }

  const handlePlayStateChange = (isPlaying) => {
    setIsAudioPlaying(isPlaying)

    if (isPlaying) {
      setIsPlayButtonFading(true)

      setShowSubtitles(true)

      if (playerMode === "srt-only" && !srtTimerRef.current) {
        srtStartTimeRef.current = Date.now() - currentAudioTime * 1000

        updateAvatarState(true)

        srtTimerRef.current = setInterval(() => {
          const newTime = (Date.now() - srtStartTimeRef.current) / 1000
          setCurrentAudioTime(newTime)

          if (subtitles.length > 0) {
            const lastSub = subtitles[subtitles.length - 1]
            if (lastSub && newTime > lastSub.end + 1) {
              setIsAudioPlaying(false)
              clearInterval(srtTimerRef.current)
              srtTimerRef.current = null
              updateAvatarState(false)
            }
          }
        }, 100)
      }

      updateAvatarState(true)
    } else {
      if (srtTimerRef.current) {
        clearInterval(srtTimerRef.current)
        srtTimerRef.current = null
      }

      updateAvatarState(false)
    }
  }

  const handleNavigate = (direction) => {
    if (isStepLocked()) {
      return
    }

    if (srtTimerRef.current) {
      clearInterval(srtTimerRef.current)
      srtTimerRef.current = null
    }

    // Narration mode: navigate by sentence (voice clip). "next" from the
    // auto-advance-on-ended lands on the next sentence's first cue.
    if (narrationEnabled) {
      const gi = currentGroupIndex
      if (direction === "next" && gi < voiceGroups.length - 1) {
        setCurrentLine(voiceGroups[gi + 1].firstCue)
      } else if (direction === "prev" && gi > 0) {
        setCurrentLine(voiceGroups[gi - 1].firstCue)
      } else if (direction === "first") setCurrentLine(0)
      return
    }

    if (direction === "next" && currentLine < totalLines - 1) {
      const nextLine = currentLine + 1
      setCurrentLine(nextLine)

      if (subtitles[nextLine]) {
        if (audioControlsRef.current && audioSrc) {
          const audio = document.querySelector("audio")
          if (audio) {
            audio.currentTime = subtitles[nextLine].start
            handleTimeUpdate(subtitles[nextLine].start)
          }
        } else if (playerMode === "srt-only") {
          setCurrentAudioTime(subtitles[nextLine].start)
          setCurrentCaption(subtitles[nextLine].text)

          if (isAudioPlaying) {
            srtStartTimeRef.current = Date.now() - subtitles[nextLine].start * 1000
            srtTimerRef.current = setInterval(() => {
              const newTime = (Date.now() - srtStartTimeRef.current) / 1000
              setCurrentAudioTime(newTime)
            }, 100)
          }
        } else {
          setCurrentCaption(subtitles[nextLine].text)
        }
      }
    } else if (direction === "prev" && currentLine > 0) {
      const prevLine = currentLine - 1
      setCurrentLine(prevLine)

      if (subtitles[prevLine]) {
        if (audioControlsRef.current && audioSrc) {
          const audio = document.querySelector("audio")
          if (audio) {
            audio.currentTime = subtitles[prevLine].start
            handleTimeUpdate(subtitles[prevLine].start)
          }
        } else if (playerMode === "srt-only") {
          setCurrentAudioTime(subtitles[prevLine].start)
          setCurrentCaption(subtitles[prevLine].text)

          if (isAudioPlaying) {
            srtStartTimeRef.current = Date.now() - subtitles[prevLine].start * 1000
            srtTimerRef.current = setInterval(() => {
              const newTime = (Date.now() - srtStartTimeRef.current) / 1000
              setCurrentAudioTime(newTime)
            }, 100)
          }
        } else {
          setCurrentCaption(subtitles[prevLine].text)
        }
      }
    } else if (direction === "first" && subtitles.length > 0) {
      setCurrentLine(0)
      if (subtitles[0]) {
        if (audioControlsRef.current && audioSrc) {
          const audio = document.querySelector("audio")
          if (audio) {
            audio.currentTime = subtitles[0].start
            handleTimeUpdate(subtitles[0].start)
          }
        } else if (playerMode === "srt-only") {
          setCurrentAudioTime(subtitles[0].start)
          setCurrentCaption(subtitles[0].text)

          if (isAudioPlaying) {
            srtStartTimeRef.current = Date.now() - subtitles[0].start * 1000
            srtTimerRef.current = setInterval(() => {
              const newTime = (Date.now() - srtStartTimeRef.current) / 1000
              setCurrentAudioTime(newTime)
            }, 100)
          }
        } else {
          setCurrentCaption(subtitles[0].text)
        }
      }
    }
  }

  const contentType = quizOptions
    ? "quiz"
    : isCelebration || isSuccess
      ? "achievements"
      : visualContent?.visualizationType || visualContent?.chartData
        ? "chartVisualization"
        : "content"

  const isFullscreenContent = visualContent?.fullscreenContent
  const isFullHeightVideo = visualContent?.videoUrl && visualContent?.fullHeightVideo

  useEffect(() => {
    const hasAudio = audioSrc || srtSrc
    const hasVideo = videoSrc
    const shouldShow = hasAudio && !hasVideo && !isCelebration && !isSuccess
    console.log("[v0] Play button visibility check:", {
      hasAudio,
      hasVideo,
      isAudioPlaying,
      isCelebration,
      isSuccess,
      shouldShow,
    })
    setShowPlayButton(shouldShow)
    if (!shouldShow) {
      setIsPlayButtonFading(false)
    }
  }, [audioSrc, srtSrc, videoSrc, isCelebration, isSuccess])

  const handlePlayButtonClick = () => {
    setIsPlayButtonFading(true)

    // Start audio playback
    if (audioControlsRef.current && audioControlsRef.current.play) {
      audioControlsRef.current.play()
    } else {
      // Fallback for SRT-only mode
      setIsAudioPlaying(true)
      handlePlayStateChange(true)
    }
  }

  const renderMobileQuizFeedback = () => {
    if (!isMobile || !quizOptions || !isChecking || selectedAnswer === null || !showFeedback) return null

    const feedbackText = quizOptions[selectedAnswer]?.feedback || ""
    const isAnswerCorrect = quizOptions[selectedAnswer]?.correct || false

    return (
      <div className="mobile-quiz-feedback active">
        <div
          className={`mobile-quiz-feedback-content ${isAnswerCorrect ? "mobile-quiz-feedback-correct" : "mobile-quiz-feedback-incorrect"}`}
        >
          <div className="mobile-quiz-feedback-text">{feedbackText}</div>
        </div>
      </div>
    )
  }

  const getAlignmentStyles = () => {
    const presets = {
      left: {
        bottom: "2rem",
        left: "2rem",
        right: "auto",
      },
      centered: {
        bottom: "2rem",
        left: "50%",
        right: "auto",
        transform: "translateX(-50%)", // Use transform for proper centering
      },
      right: {
        bottom: "2rem",
        right: "2rem",
        left: "auto",
      },
    }

    // Start with alignment preset
    const baseStyles = presets[avatarAlignment] || presets.right

    const styles = {
      ...baseStyles,
      "--avatar-scale": avatarScale,
      "--mobile-scale": mobileScale || 1.2,
    }

    // On mobile, use mobilePosition if provided, otherwise use smart defaults
    if (isMobile) {
      if (mobilePosition) {
        // User provided custom mobile positioning in JSON - use it
        Object.assign(styles, mobilePosition)
      } else {
        // No custom mobile positioning - use smart defaults based on alignment
        if (avatarAlignment === "centered") {
          styles.left = "50%"
          styles.right = "auto"
          styles.bottom = "1.5rem"
          styles.transform = "translateX(-50%)" // Use transform instead of marginLeft for consistent centering
        } else {
          // Default to bottom right for left/right alignments on mobile
          styles.right = "0.5rem"
          styles.bottom = "1rem"
          styles.left = "auto"
        }
      }
    } else {
      // Desktop: use avatarPosition if provided to override preset
      if (avatarPosition) {
        Object.assign(styles, avatarPosition)
      }
    }

    return styles
  }

  if (isStepLocked()) {
    return (
      <div className="avatar-container">
        <div className="layout-standard">
          <div className="content-column-fullwidth">
            <div className="content-container">
              <PasswordPrompt onPasswordSubmit={handleStepPasswordSubmit} lessonTitle="Step Content" />
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className={`avatar-container ${isVisualTheme ? "visual-theme" : ""} ${quizOptions && isMobile ? "quiz-content" : ""}`}
    >
      {showPlayButton && !isStepLocked() && (
        <div className={`play-button-overlay ${isPlayButtonFading ? "fade-out" : ""}`} onClick={handlePlayButtonClick}>
          <button className="play-button-overlay-button" aria-label="Start audio playback">
            <img src="/images/play-button.png" alt="Play" className="play-button-overlay-image" />
          </button>
        </div>
      )}

      <div className={`layout-standard`}>
        {isCelebration || isSuccess ? (
          <div className="celebration-container">
            <div ref={contentContainerRef} className="celebration-content">
              <Confetti count={75} />

              <h2 className="celebration-title">{mainContent || visualContent?.title || "Goed Gedaan! 🎉"}</h2>

              <div className="celebration-avatar-space">
                <AnimatePresence>
                  {currentCaption && showSubtitles && (
                    <motion.div
                      ref={bubbleRef}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 10 }}
                      className="speech-bubble text-center after:left-1/2 after:-translate-x-1/2 active"
                    >
                      <motion.p
                        key={currentCaption}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="speech-bubble-text"
                      >
                        {currentCaption || "Je hebt alle badges verdiend! Geweldig werk!"}
                      </motion.p>
                    </motion.div>
                  )}
                </AnimatePresence>

                <motion.div
                  onClick={() => {
                    const newPlayingState = !isAudioPlaying
                    setIsAudioPlaying(newPlayingState)
                    handlePlayStateChange(newPlayingState)
                    setShowSubtitles(true)
                  }}
                  className="avatar-button avatar-play-button"
                  style={{
                    "--avatar-scale": avatarScale,
                  }}
                >
                  <RiveComponent style={{ width: "100%", height: "100%" }} />
                </motion.div>

                {!hideAudioControls && (
                  <EnhancedAudioControls
                    ref={audioControlsRef}
                    audioSrc={playbackSrc}
                    autoAdvance={narrationEnabled}
                    videoSrc={videoSrc}
                    videoType={videoType}
                    srtSrc={srtSrc}
                    lessonId={lessonId}
                    onPlayStateChange={handlePlayStateChange}
                    onSubtitlesToggle={() => setShowSubtitles(!showSubtitles)}
                    onTimeUpdate={handleTimeUpdate}
                    onNavigate={handleNavigate}
                    hasNextLine={hasNextClip}
                    hasPrevLine={hasPrevClip}
                    initialShowControls={true}
                    isMobile={window.innerWidth < 768}
                  />
                )}
              </div>

              <div className="celebration-badges">
                {visualContent?.achievements &&
                  visualContent.achievements.map((achievement, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 + index * 0.15 }}
                      className="celebration-badge"
                    >
                      <span className="celebration-badge-icon">{achievement.icon}</span>
                      <span className="celebration-badge-text">{achievement.title}</span>
                    </motion.div>
                  ))}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className={isFullscreenContent ? "content-column-fullwidth" : "content-column"}>
              <div
                ref={contentContainerRef}
                className={`content-container ${isFullHeightVideo ? "content-container-fullheight" : "content-container-standard"}`}
              >
                <SuperSimpleSynchronizer
                  key={`synchronizer-${lessonId}`}
                  type={contentType}
                  content={{
                    ...visualContent,
                    title: mainContent || visualContent?.title,
                    options: quizOptions,
                    question: visualContent?.question,
                    subQuestion: visualContent?.subQuestion,
                    achievements: visualContent?.achievements,
                    fullHeightVideo: isFullHeightVideo,
                    videoUrl: videoSrc || visualContent?.videoUrl || null,
                    videoSrc: videoSrc || visualContent?.videoSrc || null,
                    videoType: videoType || visualContent?.videoType || null,
                  }}
                  audioTime={currentAudioTime}
                  isPlaying={isAudioPlaying}
                  srtSrc={srtSrc}
                  currentLine={currentLine}
                  onSelectAnswer={onSelectAnswer}
                  selectedAnswer={selectedAnswer}
                  isChecking={isChecking}
                  videoContainerRef={videoContainerRef}
                  isFullscreen={isFullscreen}
                  toggleFullscreen={toggleFullscreen}
                  onStepChange={(nextStep) => {
                    if (visualContent && typeof visualContent.onStepChange === "function") {
                      visualContent.onStepChange(nextStep)
                    }
                  }}
                />
              </div>
            </div>

            {!isFullscreenContent && (
              <div className="avatar-column">
                <div className="avatar-fixed-container" style={getAlignmentStyles()} data-alignment={avatarAlignment}>
                  <AnimatePresence mode="wait">
                    {((showFeedback && isChecking && selectedAnswer !== null && quizOptions) ||
                      (currentCaption && showSubtitles)) && (
                      <motion.div
                        ref={bubbleRef}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 10 }}
                        className={`speech-bubble speech-bubble-${avatarAlignment} ${isChecking && showFeedback ? "speech-bubble-checking" : ""} active`}
                        data-alignment={avatarAlignment}
                      >
                        <motion.p
                          key={showFeedback && isChecking ? quizOptions[selectedAnswer]?.feedback : currentCaption}
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ duration: 0.3 }}
                          className="speech-bubble-text"
                        >
                          {showFeedback && isChecking && selectedAnswer !== null && quizOptions
                            ? quizOptions[selectedAnswer]?.feedback || currentCaption
                            : currentCaption}
                        </motion.p>

                        {totalLines > 1 && !showFeedback && (
                          <div className="speech-bubble-indicators">
                            {Array.from({ length: totalLines }).map((_, i) => (
                              <div
                                key={i}
                                className={`bubble-indicator ${i === currentLine ? "bubble-indicator-active" : ""}`}
                              />
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="avatar-wrapper">
                    <motion.button
                      onClick={() => {
                        const newPlayingState = !isAudioPlaying
                        setIsAudioPlaying(newPlayingState)
                        handlePlayStateChange(newPlayingState)
                      }}
                      className="avatar-button avatar-play-button"
                      style={{
                        "--avatar-scale": avatarScale,
                      }}
                    >
                      <RiveComponent style={{ width: "100%", height: "100%" }} />
                    </motion.button>
                  </div>
                </div>

                {!hideAudioControls && (
                  <div className="avatar-controls-wrapper" data-alignment={avatarAlignment}>
                    <EnhancedAudioControls
                      ref={audioControlsRef}
                      audioSrc={playbackSrc}
                      autoAdvance={narrationEnabled}
                      videoSrc={videoSrc}
                      videoType={videoType}
                      srtSrc={srtSrc}
                      lessonId={lessonId}
                      onPlayStateChange={handlePlayStateChange}
                      onSubtitlesToggle={() => setShowSubtitles(!showSubtitles)}
                      onTimeUpdate={handleTimeUpdate}
                      onNavigate={handleNavigate}
                      hasNextLine={hasNextClip}
                      hasPrevLine={hasPrevClip}
                      initialShowControls={true}
                      isMobile={window.innerWidth < 768}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {renderMobileQuizFeedback()}
    </div>
  )
}

export default TeachingAvatar
